/**
 * QoC Offline Assessment Template Generator
 *
 * Creates a pre-populated Excel file for a facility's assigned clauses
 * for a given period. The assessor fills it offline and imports it back.
 *
 * Structure mirrors the Phocweni tool exactly so import works seamlessly.
 */
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import moment from 'moment';

// Clause metadata
const CLAUSE_META = {
    AACC: { title:'Access, Assessment and Continuity of Care',           color:'FF0EA5E9', num:1  },
    COP:  { title:'Care of the Patient',                                  color:'FF8B5CF6', num:2  },
    MOM:  { title:'Management of Medication',                             color:'FFF59E0B', num:3  },
    PRPE: { title:"Patient Rights and Patient Education",                  color:'FF10B981', num:4  },
    IPCM: { title:'Infection Prevention and Control Management',           color:'FFEF4444', num:5  },
    PSQI: { title:'Patient Safety and Quality Improvement',               color:'FF3B82F6', num:6  },
    GROM: { title:'Governance, Roles and Responsibilities of Management', color:'FF6366F1', num:7  },
    FMS:  { title:'Facility Management, Safety and Support Services',     color:'FFEC4899', num:8  },
    HRE:  { title:'Human Resource and Education',                         color:'FF14B8A6', num:9  },
    ISM:  { title:'Information System Management',                        color:'FFA855F7', num:10 },
};

// Score options for dropdown validation
const SCORE_OPTIONS = ['Met', 'Partially Met', 'Not Met', 'Not Applicable', 'Not Checked'];
const CRITICALITY_OPTIONS = ['High', 'Medium', 'Low', 'N/A'];

function hex(argb) {
    // Returns just the RGB part for border colors
    return argb.slice(2);
}

function styleHeader(cell, argbColor, fontSize = 10) {
    cell.fill   = { type:'pattern', pattern:'solid', fgColor:{ argb: argbColor } };
    cell.font   = { bold:true, color:{ argb:'FFFFFFFF' }, size: fontSize, name:'Calibri' };
    cell.alignment = { vertical:'middle', horizontal:'center', wrapText:true };
    cell.border = allBorders('FFB0B0B0');
}

function allBorders(argb = 'FFD0D0D0') {
    const s = { style:'thin', color:{ argb } };
    return { top:s, bottom:s, left:s, right:s };
}

function styleCell(cell, bgArgb = 'FFFFFFFF', bold = false, color = 'FF1a1a2e') {
    cell.fill = { type:'pattern', pattern:'solid', fgColor:{ argb: bgArgb } };
    cell.font = { bold, color:{ argb: color }, size:9, name:'Calibri' };
    cell.alignment = { vertical:'middle', wrapText:true };
    cell.border = allBorders();
}

function addDropdown(ws, rowNum, col, options) {
    const addr = `${col}${rowNum}`;
    ws.getCell(addr).dataValidation = {
        type:       'list',
        allowBlank: true,
        formulae:   [`"${options.join(',')}"`],
        showErrorMessage: true,
        error: 'Please select a valid option',
        errorTitle: 'Invalid Entry',
    };
}

/**
 * Main export function
 * @param {Object} params
 * @param {Object} params.facility     - facility object { id, business_name, site_type, located_region, ... }
 * @param {Array}  params.schedules    - schedule objects for this facility+period (each has clause_code, clause_title, scheduled_date, lead_assessor_name)
 * @param {Object} params.mesByClause  - { AACC: [...mes], COP: [...mes], ... }
 * @param {String} params.periodLabel  - e.g. "FY2025/26"
 * @param {Object} params.period       - { period_start, period_end }
 */
export async function exportAssessmentTemplate({
    facility, schedules, mesByClause, periodLabel, period
}) {
    const wb = new ExcelJS.Workbook();
    wb.creator  = 'QoC Platform';
    wb.created  = new Date();
    wb.modified = new Date();
    wb.properties.date1904 = false;

    const clauseCodes = schedules
        .filter(s => s.clause_code)
        .map(s => s.clause_code)
        .filter((v, i, a) => a.indexOf(v) === i) // unique
        .sort((a, b) => (CLAUSE_META[a]?.num||99) - (CLAUSE_META[b]?.num||99));

    // ══════════════════════════════════════════════════════════
    // SHEET 1 — INSTRUCTIONS
    // ══════════════════════════════════════════════════════════
    const instr = wb.addWorksheet('Instructions', {
        sheetProtection: { sheet: true, formatCells: false, password: '' }
    });
    instr.getColumn(1).width = 100;

    const instrRows = [
        ['ESWATINI QUALITY OF CARE (QoC) STANDARDS AUDIT — OFFLINE ASSESSMENT TEMPLATE'],
        [''],
        [`Facility:        ${facility.business_name}`],
        [`Type:            ${facility.site_type || 'N/A'}`],
        [`Region:          ${facility.located_region || 'N/A'}`],
        [`Period:          ${periodLabel}`],
        [`Window:          ${period ? moment(period.period_start).format('DD MMM YYYY') + ' — ' + moment(period.period_end).format('DD MMM YYYY') : 'N/A'}`],
        [`Clauses assigned: ${clauseCodes.join(', ')}`],
        [''],
        ['HOW TO USE THIS TEMPLATE:'],
        ['  1. Each clause tab (AACC, COP, MOM, ...) contains all Measurable Elements (MEs) for that clause.'],
        ['  2. For each ME, click the FULFILLMENT cell (column C) and select from the dropdown:'],
        ['       • Met            — Fully meets the requirement (scores 100%)'],
        ['       • Partially Met  — Partially meets the requirement (scores 50%)'],
        ['       • Not Met        — Does not meet the requirement (scores 0%)'],
        ['       • Not Applicable — Does not apply to this facility (excluded from score)'],
        ['       • Not Checked    — Could not be assessed (excluded from score)'],
        ['  3. When score is NOT Met, Partially Met, or Not Applicable — add a COMMENT (column D).'],
        ['  4. Optionally record: General Observation, Criticality, Area of Good Practice.'],
        ['  5. DO NOT change Ref No, ME Text or sheet structure — the import depends on exact column order.'],
        ['  6. DO NOT rename or delete the clause tabs.'],
        ['  7. Save and return the file to the QoC platform via: Assessments → Import from Excel.'],
        [''],
        ['SCORING FORMULA:'],
        ['  A = Total audited MEs (Met + Partially Met + Not Met only — NA and NC excluded)'],
        ['  B = 100 ÷ A  (score per Met ME)'],
        ['  Clause Score = (Met × B) + (Partially Met × B ÷ 2)'],
        ['  Overall Score = Mean of all clause scores'],
        [''],
        ['RATING SCALE:'],
        ['  98 – 100%   Outstanding        ★★★★★   Ready for ISO accreditation'],
        ['  95 – 97.9%  Excellent          ★★★★    Ready for ISO accreditation'],
        ['  90 – 94.9%  Good               ★★★     Almost ready for ISO accreditation'],
        ['  85 – 89.9%  Fair               ★★      Somewhat ready for ISO accreditation'],
        ['  70 – 84.9%  Requires Improvement ★     Not ready for ISO accreditation'],
        ['  < 70%       Inadequate         —        Cause for concern'],
        [''],
        ['KEY CRITICAL STANDARDS (KCS):'],
        ['  MEs marked ⚠ KCS are Key Critical Standards — these MUST BE MET.'],
        ['  Failure on KCS is flagged immediately to the national office.'],
    ];

    instrRows.forEach((row, i) => {
        const r = instr.addRow(row);
        r.height = 18;
        const cell = r.getCell(1);
        if (i === 0) {
            cell.font = { bold:true, size:14, color:{argb:'FF0F172A'}, name:'Calibri' };
            cell.fill = { type:'pattern', pattern:'solid', fgColor:{argb:'FFDBEDFF'} };
            r.height = 28;
        } else if (row[0].startsWith('HOW TO') || row[0].startsWith('SCORING') ||
                   row[0].startsWith('RATING') || row[0].startsWith('KEY')) {
            cell.font = { bold:true, size:11, color:{argb:'FF1890FF'}, name:'Calibri' };
        } else if (row[0].startsWith('  ')) {
            cell.font = { size:9, color:{argb:'FF475569'}, name:'Calibri' };
        } else if (row[0].startsWith('Facility') || row[0].startsWith('Type') ||
                   row[0].startsWith('Region') || row[0].startsWith('Period') ||
                   row[0].startsWith('Window') || row[0].startsWith('Clauses')) {
            cell.font = { bold:true, size:10, color:{argb:'FF0F172A'}, name:'Calibri' };
            cell.fill = { type:'pattern', pattern:'solid', fgColor:{argb:'FFF8FAFF'} };
        }
    });

    // ══════════════════════════════════════════════════════════
    // SHEET 2 — COVER PAGE (for reference / printing)
    // ══════════════════════════════════════════════════════════
    const cover = wb.addWorksheet('Cover Page');
    cover.columns = [
        {width:24},{width:32},{width:24},{width:32}
    ];

    // MoH header
    cover.mergeCells('A1:D1');
    Object.assign(cover.getCell('A1'), {
        value: 'Eswatini Ministry of Health — Quality of Care Standards Audit',
    });
    styleHeader(cover.getCell('A1'), 'FF0F172A', 14);
    cover.getRow(1).height = 32;

    cover.mergeCells('A2:D2');
    Object.assign(cover.getCell('A2'), {
        value: `ASSESSMENT TEMPLATE — ${facility.business_name} — Period: ${periodLabel}`,
    });
    styleHeader(cover.getCell('A2'), 'FF1890FF', 11);
    cover.getRow(2).height = 22;

    cover.addRow([]);

    // Facility info
    [
        ['Facility Name',    facility.business_name,      'Period',         periodLabel],
        ['Type of Facility', facility.site_type||'N/A',   'Window Start',   period ? moment(period.period_start).format('DD MMM YYYY') : ''],
        ['Region',           facility.located_region||'', 'Window End',     period ? moment(period.period_end).format('DD MMM YYYY')   : ''],
        ['Facility Code',    facility.fac_code||'',       'Clauses in this template', clauseCodes.join(', ')],
    ].forEach(row => {
        const r = cover.addRow(row);
        r.height = 20;
        styleCell(r.getCell(1), 'FFF0F5FF', true,  'FF475569');
        styleCell(r.getCell(2), 'FFFAFBFF', false, 'FF0F172A');
        styleCell(r.getCell(3), 'FFF0F5FF', true,  'FF475569');
        styleCell(r.getCell(4), 'FFFAFBFF', false, 'FF0F172A');
    });

    cover.addRow([]);

    // Assessor table
    const asmtHdr = cover.addRow(['Role', 'Name', 'Signature', 'Date']);
    asmtHdr.height = 20;
    ['A','B','C','D'].forEach(col => styleHeader(cover.getCell(`${col}${asmtHdr.number}`), 'FF0F172A', 9));
    ['Lead Assessor', 'Auditor 1', 'Auditor 2', 'Reviewer'].forEach(role => {
        const r = cover.addRow([role, '', '', '']);
        r.height = 22;
        styleCell(r.getCell(1), 'FFF8FAFF', true);
        [2,3,4].forEach(c => styleCell(r.getCell(c)));
    });

    cover.addRow([]);

    // Clause summary table (to be filled on return)
    const sumHdr = cover.addRow(['#', 'Clause', 'Score %', 'Rating', 'Notes']);
    sumHdr.height = 20;
    cover.mergeCells(`A${sumHdr.number}:A${sumHdr.number}`);
    ['A','B','C','D','E'].forEach(col => styleHeader(cover.getCell(`${col}${sumHdr.number}`), 'FF1890FF', 9));
    cover.getColumn('B').width = 45;
    cover.getColumn('E').width = 40;

    clauseCodes.forEach(code => {
        const m = CLAUSE_META[code];
        const sched = schedules.find(s => s.clause_code === code);
        const r = cover.addRow([
            m?.num || '',
            `${code} — ${m?.title || ''}`,
            '',  // filled after audit
            '',  // filled after audit
            `Lead: ${sched?.lead_assessor_name || 'TBA'}  |  Scheduled: ${sched?.scheduled_date ? moment(sched.scheduled_date).format('DD MMM YYYY') : 'N/A'}`,
        ]);
        r.height = 18;
        styleCell(r.getCell(1), 'FFF8FAFF', true);
        styleCell(r.getCell(2), 'FFF8FAFF', true,
            m ? 'FF' + hex(m.color) : 'FF333333');
        styleCell(r.getCell(3));
        styleCell(r.getCell(4));
        styleCell(r.getCell(5), 'FFFAFBFF', false, 'FF888888');
    });

    // ══════════════════════════════════════════════════════════
    // SHEETS — ONE PER ASSIGNED CLAUSE
    // ══════════════════════════════════════════════════════════
    clauseCodes.forEach(code => {
        const meta  = CLAUSE_META[code];
        const mes   = mesByClause[code] || [];
        const sched = schedules.find(s => s.clause_code === code);
        if (!meta) return;

        const ws = wb.addWorksheet(`${meta.num}. ${code}`, {
            pageSetup: { paperSize:9, orientation:'landscape', fitToPage:true, fitToWidth:1 },
        });

        // ── Row 1: Clause title banner ──────────────────────────────────────
        ws.mergeCells('A1:I1');
        ws.getCell('A1').value = `Standard ${meta.num}. ${code} — ${meta.title}`;
        styleHeader(ws.getCell('A1'), meta.color, 13);
        ws.getRow(1).height = 30;

        // ── Row 2: Facility + Schedule info ─────────────────────────────────
        ws.mergeCells('A2:E2');
        ws.getCell('A2').value = `${facility.business_name}  •  Period: ${periodLabel}  •  Level: ${facility.site_type||'N/A'}`;
        ws.getCell('A2').font = { size:10, color:{argb:'FF475569'}, name:'Calibri' };
        ws.mergeCells('F2:I2');
        ws.getCell('F2').value = `Scheduled: ${sched?.scheduled_date ? moment(sched.scheduled_date).format('DD MMM YYYY') : 'N/A'}  •  Lead: ${sched?.lead_assessor_name||'TBA'}`;
        ws.getCell('F2').font  = { size:10, color:{argb:'FF475569'}, name:'Calibri', italic:true };
        ws.getCell('F2').alignment = { horizontal:'right' };
        ws.getRow(2).height = 18;

        // ── Row 3: Scoring summary (auto-calculates via Excel formulas) ──────
        ws.mergeCells('A3:I3');
        ws.getCell('A3').value = 'Fill in the FULFILLMENT column for each ME below. Dropdowns are provided. Red cells = Key Critical Standards (KCS).';
        ws.getCell('A3').font = { italic:true, size:9, color:{argb:'FF666666'}, name:'Calibri' };
        ws.getCell('A3').fill = { type:'pattern', pattern:'solid', fgColor:{argb:'FFFFFFCC'} };
        ws.getRow(3).height = 16;

        ws.addRow([]); // spacer row 4

        // ── Row 5: Column headers ────────────────────────────────────────────
        const COL_HEADERS = [
            'Ref No',
            'Measurable Element\n(Do not edit)',
            'Fulfillment\n★ SELECT FROM DROPDOWN ★',
            'Comment\n(Required if not Met)',
            'General Observation',
            'Criticality of Observation',
            'Area of Good Practice',
            'Evidence Notes',
            'KCS',
        ];
        const headerRow = ws.addRow(COL_HEADERS);
        headerRow.height = 36;
        headerRow.eachCell((cell, cn) => styleHeader(cell, meta.color, 9));

        // ── Column widths ────────────────────────────────────────────────────
        ws.getColumn(1).width = 13;   // Ref No
        ws.getColumn(2).width = 50;   // ME text — wide
        ws.getColumn(3).width = 18;   // Fulfillment — dropdown
        ws.getColumn(4).width = 30;   // Comment
        ws.getColumn(5).width = 28;   // General Observation
        ws.getColumn(6).width = 18;   // Criticality
        ws.getColumn(7).width = 28;   // Good Practice
        ws.getColumn(8).width = 28;   // Evidence Notes
        ws.getColumn(9).width = 10;   // KCS flag

        // ── ME rows ──────────────────────────────────────────────────────────
        mes.forEach((me, idx) => {
            const isKCS = parseInt(me.is_critical) === 1;
            const evenBg = idx % 2 === 0 ? 'FFFAFBFF' : 'FFFFFFFF';
            const kcsRefColor = isKCS ? 'FFEF4444' : 'FF' + hex(meta.color);

            const row = ws.addRow([
                me.ref_no || '',
                me.me_text || '',
                '',   // Fulfillment — user fills this
                '',   // Comment
                '',   // General Observation
                '',   // Criticality
                '',   // Good Practice
                '',   // Evidence Notes
                isKCS ? '⚠ KCS' : '',
            ]);
            row.height = 40;

            // Style each cell
            row.eachCell((cell, cn) => {
                const bg = isKCS ? 'FFFFF8F8' : evenBg;
                styleCell(cell, bg);
            });

            // Ref No — bold, colored
            row.getCell(1).font = { bold:true, color:{argb: kcsRefColor}, size:9, name:'Calibri' };
            row.getCell(1).alignment = { horizontal:'center', vertical:'middle' };

            // ME Text — left aligned, no edit hint color
            row.getCell(2).font = { size:9, color:{argb:'FF0F172A'}, name:'Calibri' };
            row.getCell(2).fill = { type:'pattern', pattern:'solid',
                fgColor:{argb: isKCS ? 'FFFFF0F0' : evenBg} };

            // Fulfillment — highlight as input cell
            row.getCell(3).fill = { type:'pattern', pattern:'solid',
                fgColor:{argb: isKCS ? 'FFFFE8E8' : 'FFEBF8FF'} };
            row.getCell(3).font = { bold:true, size:9, name:'Calibri' };
            row.getCell(3).alignment = { horizontal:'center', vertical:'middle' };
            // Add dropdown validation
            addDropdown(ws, row.number, 'C', SCORE_OPTIONS);

            // Criticality — dropdown
            row.getCell(6).fill = { type:'pattern', pattern:'solid', fgColor:{argb:'FFFFFFF0'} };
            addDropdown(ws, row.number, 'F', CRITICALITY_OPTIONS);

            // KCS column
            if (isKCS) {
                row.getCell(9).font = { bold:true, color:{argb:'FFEF4444'}, size:9, name:'Calibri' };
                row.getCell(9).alignment = { horizontal:'center', vertical:'middle' };
            }
        });

        // ── Auto-filter + freeze panes ───────────────────────────────────────
        ws.autoFilter = {
            from: { row: headerRow.number, column:1 },
            to:   { row: headerRow.number, column:9 },
        };
        ws.views = [{ state:'frozen', xSplit:0, ySplit: headerRow.number }];

        // ── Protect sheet: allow editing only in data entry columns ──────────
        // (columns C, D, E, F, G, H are unlocked; A, B, I are locked)
        ws.protect('', {
            selectLockedCells:   true,
            selectUnlockedCells: true,
            formatCells: false,
            formatColumns: false,
            formatRows: false,
            insertColumns: false,
            insertRows: false,
            deleteColumns: false,
            deleteRows: false,
        });
        // Unlock input cells
        mes.forEach((me, idx) => {
            const rn = headerRow.number + 1 + idx;
            ['C','D','E','F','G','H'].forEach(col => {
                ws.getCell(`${col}${rn}`).protection = { locked: false };
            });
        });
    });

    // ── Generate and download ─────────────────────────────────────────────────
    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const fName = `QoC_Template_${facility.business_name.replace(/\s+/g,'_')}_${periodLabel.replace(/\//g,'-')}.xlsx`;
    saveAs(blob, fName);
    return fName;
}
